import React from 'react';
import styled from '@emotion/styled';
import { colors } from 'src/theme';

interface ToggleOptionItem {
  value: any;
  label: string;
  icon?: React.ReactNode;
}

interface ToggleButtonGroupProps {
  options: ToggleOptionItem[];
  value: any;
  onChange: (value: any) => void;
  label?: string;
  disabled?: boolean;
}

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  width: '100%'
});

const Label = styled.label({
  fontFamily: "'Red Hat Text', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
  color: colors.text1,
  overflow: 'hidden',
  textOverflow: 'ellipsis'
});

const ToggleContainer = styled.div({
  position: 'relative',
  display: 'flex',
  alignItems: 'stretch',
  width: '100%',
  height: '40px',
  backgroundColor: colors.text5,
  borderRadius: '8px',
  padding: '4px',
  boxSizing: 'border-box',
});

/** Pill uses same track math as flex row: inset padding + equal fr-like segments */
const SlidingPill = styled.div<{ $optionCount: number; $selectedIndex: number }>(
  ({ $optionCount, $selectedIndex }) => ({
    position: 'absolute',
    top: 4,
    bottom: 4,
    left: `calc(4px + (100% - 8px) * ${$selectedIndex} / ${$optionCount})`,
    width: `calc((100% - 8px) / ${$optionCount})`,
    backgroundColor: colors.white,
    borderRadius: '4px',
    boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    transition: 'left 0.28s cubic-bezier(0.4, 0, 0.2, 1), width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'none',
    zIndex: 0,
  })
);

const ToggleRow = styled.div({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'stretch',
  flex: '1 1 auto',
  minWidth: 0,
  width: '100%',
  height: '100%',
});

const ToggleOptionButton = styled.button<{ isSelected: boolean }>(({ isSelected }) => ({
  flex: '1 1 0',
  minWidth: 0,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: '8px',
  padding: '8px 16px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',

  '&:hover': {
    backgroundColor: isSelected ? 'transparent' : 'rgba(255, 255, 255, 0.45)'
  },

  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.6
  }
}));

const OptionText = styled.span<{ isSelected: boolean }>(({ isSelected }) => ({
  fontFamily: "'Red Hat Text', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: isSelected ? colors.text2 : colors.text3,
  transition: 'color 0.2s ease',
}));

export const ToggleButtonGroup: React.FC<ToggleButtonGroupProps> = ({
  options,
  value,
  onChange,
  label,
  disabled = false
}) => {
  const optionCount = options.length;
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value)
  );

  const handleSelect = (optionValue: any) => {
    if (!disabled) {
      onChange(optionValue);
    }
  };

  return (
    <Container>
      {label && <Label>{label}</Label>}
      <ToggleContainer>
        {optionCount > 0 && (
          <SlidingPill $optionCount={optionCount} $selectedIndex={selectedIndex} />
        )}
        <ToggleRow>
          {options.map((option) => (
            <ToggleOptionButton
              key={String(option.value)}
              type="button"
              isSelected={value === option.value}
              onClick={() => handleSelect(option.value)}
              disabled={disabled}
            >
              <OptionText isSelected={value === option.value}>
                {option.label}
              </OptionText>
              {option.icon && option.icon}
            </ToggleOptionButton>
          ))}
        </ToggleRow>
      </ToggleContainer>
    </Container>
  );
};
